import { useState, useCallback, useEffect } from 'react'
import axios from 'axios'

import { ErrorPage } from '../../../ErrorPage'
import { Wallpaper } from '../../../../components/Wallpaper'
import { RetryDeviceScanDialog } from '../../../Login/RetryDeviceScanDialog'
import { useThemeContext } from '../../../../contexts/ThemeContext'
import { getOrCreateBrowserKey } from '../../../../utils/fingerprint_utils'
import { PrivatePayrollAuctionLoading } from '../Loading'

export const PrivatePayrollAuctionDeviceScan = (props: any) => {
    const { clientThemeSettings } = useThemeContext()
    const { signData, setSignerStep, setSignData, isBatch } = props

    const [loading, setLoading] = useState(true)
    const [deviceScanHasMadeRequest, setDeviceScanHasMadeRequest] = useState(false)
    const [openRetryDeviceScanDialog, setOpenRetryDeviceScanDialog] = useState(false)

    const eventType = 'QiSign'
    const eventId = '1'
    const endpoint = isBatch ? '/signer_batch' : '/signer'

    const initialError = { has_error: false, status: 0 }
    const [error, setError] = useState(initialError)

    const sendDeviceScanRequest = useCallback(async () => {
        if (!signData.signer_token || deviceScanHasMadeRequest) return

        if (!(window as any).vPkg?.DeviceScan) {
            setOpenRetryDeviceScanDialog(true)
        }

        const vPkg = (window as any).vPkg
        const deviceScan =
            process.env.REACT_APP_ENV === 'production'
                ? new vPkg.DeviceScan(signData.device_token, signData.session_id)
                : new vPkg.DeviceScan(signData.device_token, signData.session_id).setSandbox()

        async function callDeviceScan() {
            await deviceScan.info(eventType, eventId).then(() => {
                setOpenRetryDeviceScanDialog(false)
            }).catch(() => {
                setOpenRetryDeviceScanDialog(true)
                throw new Error('Failed Device Scan')
            })
        }

        try {
            await callDeviceScan()
        } catch {
            return
        }

        let data: Record<string, any>
        if (isBatch) {
            data = {
                event_date: new Date().toISOString(),
                step: 'device_scan',
                session_id: signData.session_id,
            }
        } else {
            let browserKey = null
            try {
                browserKey = await getOrCreateBrowserKey()
            } catch {}
            data = {
                event_date: new Date().toISOString(),
                step: 'device_scan',
                session_id: signData.session_id,
                device_data: { device_key: browserKey },
            }
        }

        const config = { headers: { Authorization: signData.signer_token } }

        try {
            const response = await axios.patch(endpoint, data, config)
            setSignerStep(response.data.next_step)
            setSignData({ ...signData, ...response.data })
            setDeviceScanHasMadeRequest(true)
        } catch (err: any) {
            const status = [401, 403, 409].includes((err.response || {}).status)
                ? err.response.status
                : 500
            setError({ has_error: true, status })
        } finally {
            setLoading(false)
        }
    }, [signData, deviceScanHasMadeRequest, setSignerStep, setSignData, endpoint, isBatch])

    useEffect(() => {
        sendDeviceScanRequest()
    }, [sendDeviceScanRequest])

    if (loading) {
        return <PrivatePayrollAuctionLoading />
    }

    return (
        <Wallpaper
            id="deviceScanContainer"
            backgroundColor={clientThemeSettings['background_color']}
        >
            {error.has_error && (
                <ErrorPage
                    error={error}
                    redirectToLogin={() => setSignerStep('login')}
                    resetError={() => setError(initialError)}
                    clientThemeSettings={clientThemeSettings}
                />
            )}
            {openRetryDeviceScanDialog && (
                <RetryDeviceScanDialog
                    open={openRetryDeviceScanDialog}
                    retryDeviceScan={sendDeviceScanRequest}
                    clientThemeSettings={clientThemeSettings}
                />
            )}
        </Wallpaper>
    )
}
